/**
 * Budget API hook'lari. Kalitlar: ["budget"] (umumiy ko'rinish, reja), ["expense"], ["salary"], ["bonus"].
 * Real-time: expense/salary/bonus/budget_plan hodisalari o'z kalitlarini yangilaydi; overview uchun useBudgetRealtime.
 */
import { useEffect } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAllPages, ApiError, type Paginated, type RequestOptions } from "@/lib/api";
import { subscribeEvents } from "@/lib/realtime";
import type {
  Bonus,
  BonusSummary,
  BudgetBranch,
  BudgetOverview,
  BudgetPlan,
  Expense,
  ExpenseSummary,
  PlacementFee,
  PlacementsResponse,
  PlanUpsertItem,
  SalariesResponse,
  Salary,
} from "./types";

type Query = RequestOptions["query"];
// Pul harakati (money_account, transaction) ham: moliya paneli ["budget", "finance"] shu yerda yangilanadi
const REALTIME_ENTITIES = new Set(["expense", "salary", "bonus", "budget_plan", "approval", "application", "money_account", "transaction"]);

export const budgetKeys = {
  all: ["budget"] as const,
  overview: (q: Query) => ["budget", "overview", q] as const,
  plans: (q: Query) => ["budget", "plans", q] as const,
  branches: ["budget", "branches"] as const,
  expenses: (q: Query) => ["expense", "list", q] as const,
  expenseSummary: (q: Query) => ["expense", "summary", q] as const,
  salaries: (q: Query) => ["salary", "list", q] as const,
  bonuses: (q: Query) => ["bonus", "list", q] as const,
  bonusSummary: (q: Query) => ["bonus", "summary", q] as const,
  // "application" -- joylashtirish o'zgarsa (driver profili, ishga olish) ro'yxat ham yangilanadi
  placements: (q: Query) => ["application", "placements", q] as const,
};

/** Budjet obyektlari o'zgarsa umumiy ko'rinish (reja/fakt) ham eskiradi; ketma-ket hodisalar bitta so'rovga yig'iladi */
export function useBudgetRealtime() {
  const qc = useQueryClient();
  useEffect(() => {
    let timer: number | undefined;
    const off = subscribeEvents((ev) => {
      if (!REALTIME_ENTITIES.has(ev.entity)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => qc.invalidateQueries({ queryKey: budgetKeys.all }), 1000);
    });
    return () => {
      window.clearTimeout(timer);
      off();
    };
  }, [qc]);
}

function invalidateBudget(qc: ReturnType<typeof useQueryClient>, ...entities: string[]) {
  qc.invalidateQueries({ queryKey: budgetKeys.all });
  entities.forEach((e) => qc.invalidateQueries({ queryKey: [e] }));
  // Xodim profili ham maosh va bonusni ko'rsatadi
  qc.invalidateQueries({ queryKey: ["user", "profile"] });
}

/** Ommaviy amal uchun: mijoz ko'rgan versiyalar (backend eskirganini 409 bilan rad etadi) */
type Versioned = { id: number; version: number };
function versioned(rows: Versioned[]) {
  return { ids: rows.map((r) => r.id), versions: Object.fromEntries(rows.map((r) => [r.id, r.version])) };
}

/** Maosh/bonus amali xatosi: 409 -- kim o'zgartirgani (ma'lumot qayta yuklanadi); boshqasi -- server matni */
export function payError(err: unknown, t: (key: string, options?: Record<string, unknown>) => string, fallback: string): string {
  if (err instanceof ApiError && err.isConflict) return err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon");
  return err instanceof ApiError ? err.message : fallback;
}

// ---- Overview, reja, filiallar ----

export function useBudgetOverview(query: Query) {
  useBudgetRealtime();
  return useQuery({
    queryKey: budgetKeys.overview(query),
    queryFn: () => api.get<BudgetOverview>("/api/budget/overview/", { query }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useBudgetPlans(query: Query, enabled = true) {
  return useQuery({
    queryKey: budgetKeys.plans(query),
    queryFn: () => getAllPages<BudgetPlan>("/api/budget/plans/", query),
    enabled,
  });
}

export function useUpsertPlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: PlanUpsertItem[]) => api.put<BudgetPlan[]>("/api/budget/plans/upsert/", items),
    onSuccess: () => invalidateBudget(qc, "dashboard"),
  });
}

export function useBudgetBranches() {
  return useQuery({
    queryKey: budgetKeys.branches,
    queryFn: () => api.get<BudgetBranch[]>("/api/budget/branches/"),
    staleTime: 10 * 60_000,
  });
}

// ---- Xarajatlar ----

export function useExpenses(query: Query) {
  return useQuery({
    queryKey: budgetKeys.expenses(query),
    queryFn: () => api.get<Paginated<Expense>>("/api/budget/expenses/", { query }),
    placeholderData: keepPreviousData,
  });
}

/** Ro'yxat ostidagi jami: o'sha filtrlar bilan, HAMMA sahifa bo'yicha, valyuta alohida (backend expenses/totals) */
export function useExpenseTotals(query: Query) {
  return useQuery({
    queryKey: ["expense", "totals", query],
    queryFn: () => api.get<{ count: number; approved_pending: { currency: string; amount: string }[]; rejected: { currency: string; amount: string }[] }>("/api/budget/expenses/totals/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useExpenseSummary(query: Query) {
  return useQuery({
    queryKey: budgetKeys.expenseSummary(query),
    queryFn: () => api.get<ExpenseSummary>("/api/budget/expenses/summary/", { query }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Multipart: chek fayli bilan. Yaratilgach owner'ga tasdiq so'rovi tushadi. */
export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => api.post<Expense>("/api/budget/expenses/", undefined, { formData }),
    onSuccess: () => invalidateBudget(qc, "expense", "approval", "counts"),
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, formData }: { id: number; data?: Record<string, unknown> & { version: number }; formData?: FormData }) =>
      formData ? api.patch<Expense>(`/api/budget/expenses/${id}/`, undefined, { formData }) : api.patch<Expense>(`/api/budget/expenses/${id}/`, data),
    onSettled: () => invalidateBudget(qc, "expense"),
  });
}

export function useArchiveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/budget/expenses/${id}/`),
    onSuccess: () => invalidateBudget(qc, "expense"),
  });
}

/** Tasdiqlangan xarajatni hisobdan to'lash: Pul bo'limiga chiqim yoziladi */
export function usePayExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, account, date, time, commission_pct }: { id: number; account: number; date: string; time?: string; commission_pct?: string }) =>
      api.post<Expense>(`/api/budget/expenses/${id}/pay/`, { account, date, time, commission_pct }),
    onSettled: () => invalidateBudget(qc, "expense", "money_account", "transaction"),
  });
}

// ---- Maoshlar ----

export function useSalaries(query: Query) {
  return useQuery({
    queryKey: budgetKeys.salaries(query),
    queryFn: () => api.get<SalariesResponse>("/api/budget/salaries/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useUpsertSalaries() {
  const qc = useQueryClient();
  return useMutation({
    // version: ko'rilgan maosh versiyasi, hali yozilmagan bo'lsa null
    mutationFn: (data: { period: string; items: { user_id: number; amount: string | number; currency?: string; note?: string; version: number | null }[] }) =>
      api.post<Salary[]>("/api/budget/salaries/upsert/", data),
    onSettled: () => invalidateBudget(qc, "salary"),
  });
}

export function useMarkSalariesPaid() {
  const qc = useQueryClient();
  return useMutation({
    // account -- qaysi hisobdan (Pul bo'limiga chiqim yoziladi); bo'lmasa faqat belgi
    mutationFn: ({ rows, paid = true, account, date, time, commission_pct }: { rows: Versioned[]; paid?: boolean; account?: number | null; date?: string; time?: string; commission_pct?: string }) =>
      api.post<{ updated: number }>("/api/budget/salaries/mark-paid/", { ...versioned(rows), paid, account: account ?? undefined, date, time, commission_pct }),
    onSettled: () => invalidateBudget(qc, "salary"),
  });
}

// ---- Bonuslar ----

export function useBonuses(query: Query) {
  return useQuery({
    queryKey: budgetKeys.bonuses(query),
    queryFn: () => api.get<Bonus[]>("/api/budget/bonuses/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useBonusSummary(query: Query) {
  return useQuery({
    queryKey: budgetKeys.bonusSummary(query),
    queryFn: () => api.get<BonusSummary>("/api/budget/bonuses/summary/", { query }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCalculateBonuses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { period: string; branch?: number }) => api.post<Bonus[]>("/api/budget/bonuses/calculate/", data),
    onSuccess: () => invalidateBudget(qc, "bonus"),
  });
}

export function useAdjustBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; adjustment: string | number; adjustment_note: string; version: number }) =>
      api.patch<Bonus>(`/api/budget/bonuses/${id}/`, data),
    onSettled: () => invalidateBudget(qc, "bonus"),
  });
}

export function useApproveBonuses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: Versioned[]) => api.post<{ approved: number }>("/api/budget/bonuses/approve/", versioned(rows)),
    onSettled: () => invalidateBudget(qc, "bonus", "approval", "dashboard"),
  });
}

export function useMarkBonusesPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rows, account, date, time, commission_pct }: { rows: Versioned[]; account?: number | null; date?: string; time?: string; commission_pct?: string }) =>
      api.post<{ paid: number }>("/api/budget/bonuses/mark-paid/", { ...versioned(rows), account: account ?? undefined, date, time, commission_pct }),
    onSettled: () => invalidateBudget(qc, "bonus"),
  });
}

// ---- Carrier to'lovlari ----

export function usePlacements(query: Query) {
  return useQuery({
    queryKey: budgetKeys.placements(query),
    queryFn: () => api.get<PlacementsResponse>("/api/budget/placements/", { query }),
    placeholderData: keepPreviousData,
  });
}

/** Shartlar: summa, muddat, izoh; holat qo'lda faqat "to'lanmadi" (yozib yuborish) yoki qaytarish */
export interface PlacementFeeUpdate {
  id: number;
  version: number;
  fee_amount?: string | null;
  fee_due_date?: string | null;
  fee_note?: string;
  fee_status?: "not_paid" | "pending";
}

function afterFeeChange(qc: ReturnType<typeof useQueryClient>) {
  invalidateBudget(qc, "application");
  qc.invalidateQueries({ queryKey: ["driver"] });
  qc.invalidateQueries({ queryKey: ["transaction"] });
  qc.invalidateQueries({ queryKey: ["money_account"] });
  qc.invalidateQueries({ queryKey: ["finance"] });
}

export function useUpdatePlacementFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: PlacementFeeUpdate) => api.patch<PlacementFee>(`/api/budget/placements/${id}/`, data),
    onSettled: () => afterFeeChange(qc),
  });
}

/** Pul tushdi: Pul daftariga kirim (hisob majburiy), holat qayta hisoblanadi */
export interface FeePaymentInput {
  id: number;
  version: number;
  amount: string;
  account: number;
  date?: string;
  time?: string;
  note?: string;
  /** Komissiya foizi ("" -- yo'q): hisobga net tushadi, to'lov holati to'liq summadan */
  commission_pct?: string;
}

export function useAddFeePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: FeePaymentInput) => api.post<PlacementFee>(`/api/budget/placements/${id}/payments/`, data),
    onSettled: () => afterFeeChange(qc),
  });
}

export function useVoidFeePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, txId, version }: { id: number; txId: number; version: number }) => api.delete<PlacementFee>(`/api/budget/placements/${id}/payments/${txId}/`, { query: { version } }),
    onSettled: () => afterFeeChange(qc),
  });
}

